"use client"
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card"
import { Brain, Sailboat, Bot, BarChart3, FileText, ArrowRight } from "lucide-react"
import { motion } from "framer-motion"
import Link from "next/link"

export interface WritingSectionPost {
  slug: string
  title: string
  description: string
  date: string
  tags: string[]
  readingTime: number
}

// Posts arrive as a prop, read from posts/*.mdx on the server. This section
// used to render a hand-maintained `articles` array in lib/data.ts, which had
// drifted: it was missing the newest post entirely, so the homepage advertised
// five essays while the blog had six.
const TAG_ICONS: Record<string, typeof Brain> = {
  'machine-learning': Brain,
  'data-science': BarChart3,
  'football-analytics': BarChart3,
  statistics: BarChart3,
  devops: Sailboat,
  sailpoint: Sailboat,
  ai: Bot,
  mcp: Bot,
}

const prettifyTag = (tag: string) =>
  tag.split('-').map((w) => (w.length <= 2 ? w.toUpperCase() : w[0].toUpperCase() + w.slice(1))).join(' ')

export default function WritingSection({ posts }: { posts: WritingSectionPost[] }) {
  if (!posts || posts.length === 0) {
    return null // Don't render if there is nothing published
  }

  return (
    <section id="blog" className="py-20 bg-gradient-to-b from-background to-secondary/30">
      <div className="container">
        <div className="text-center mb-12">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            // Matches the other section headings, which step up from text-2xl.
            // This one started at text-4xl and dominated the phone viewport.
            className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/80"
          >
            Published Articles
          </motion.h2>
          <motion.p 
            className="text-lg text-muted-foreground max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            My published articles on artificial intelligence, machine learning, and technology trends.
          </motion.p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {posts.map((post, index) => {
            const primaryTag = post.tags[0]
            const Icon = TAG_ICONS[primaryTag] ?? FileText
            return (
              <motion.div
                key={post.slug}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link href={`/blog/${post.slug}`} className="block h-full">
                  <Card className="h-full flex flex-col group hover:shadow-lg transition-all duration-300 border-[hsl(var(--border))]/50 hover:border-primary/30 overflow-hidden cursor-pointer">
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 rounded-lg bg-primary/10 text-primary">
                          <Icon className="h-5 w-5" />
                        </div>
                        {primaryTag && (
                          <span className="text-xs font-medium text-primary px-2 py-1 bg-primary/10 rounded-full">
                            {prettifyTag(primaryTag)}
                          </span>
                        )}
                      </div>
                      <CardTitle className="text-xl font-bold mb-2 group-hover:text-primary transition-colors">
                        {post.title}
                      </CardTitle>
                      {post.description && (
                        <CardDescription className="text-muted-foreground line-clamp-3">
                          {post.description}
                        </CardDescription>
                      )}
                    </CardHeader>
                    <CardContent className="flex-grow flex items-end pb-4">
                      <div className="flex w-full justify-between items-center">
                        <span className="text-sm text-muted-foreground">
                          {new Date(post.date).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                          {' · '}
                          {post.readingTime} min read
                        </span>
                        <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
